<template>
  <div class="modal fade leave-application-modal" id="leave-application-modal" data-bs-backdrop="false" tabindex="-1" aria-labelledby="leaveApplicationLabel" aria-hidden="true">
    <div class="modal-dialog modal-dialog-centered modal-lg">
      <div class="modal-content" style="box-shadow: 0 10px 40px rgba(0,0,0,0.3);">
        <div class="modal-header bg-primary text-white">
          <h5 class="modal-title text-white" id="leaveApplicationLabel">
            <i class="mdi mdi-calendar-plus me-2"></i>Apply for Leave
          </h5>
          <button type="button" class="btn-close btn-close-white" @click="resetForm" data-bs-dismiss="modal" aria-label="Close"></button>
        </div>

        <form @submit.prevent="submitLeaveRequest">
          <div class="modal-body">
            <!-- Leave Type Selection -->
            <div class="row mb-3">
              <div class="col-md-6">
                <label for="leaveType" class="form-label">Leave Type <span class="text-danger">*</span></label>
                <select
                    class="form-select"
                    id="leaveType"
                    v-model="formData.leaveTypeID"
                    @change="onLeaveTypeChange"
                    required
                >
                  <option value="">Select Leave Type</option>
                  <option
                      v-for="type in leaveTypes"
                      :key="type.id"
                      :value="type.id"
                  >
                    {{ type.typeName }} ({{ getAvailableDaysForType(type.id) }} days available)
                  </option>
                </select>
                <small v-if="selectedLeaveType" class="text-muted">
                  Available Days: {{ getAvailableDays() }}
                </small>
              </div>

              <div class="col-md-6">
                <label class="form-label">Duration Type <span class="text-danger">*</span></label>
                <div class="d-flex gap-3">
                  <div class="form-check">
                    <input
                        class="form-check-input"
                        type="radio"
                        id="fullDay"
                        value="full"
                        v-model="durationType"
                        checked
                    >
                    <label class="form-check-label" for="fullDay">Full Day(s)</label>
                  </div>
                  <div class="form-check">
                    <input
                        class="form-check-input"
                        type="radio"
                        id="halfDay"
                        value="half"
                        v-model="durationType"
                    >
                    <label class="form-check-label" for="halfDay">Half Day</label>
                  </div>
                </div>
              </div>
            </div>

            <!-- Date Selection -->
            <div class="row mb-3">
              <div class="col-md-6">
                <label for="startDate" class="form-label">Start Date <span class="text-danger">*</span></label>
                <input
                    type="date"
                    class="form-control"
                    id="startDate"
                    v-model="formData.startDate"
                    :min="minDate"
                    @change="calculateDays"
                    required
                >
              </div>

              <div class="col-md-6">
                <label for="endDate" class="form-label">
                  {{ durationType === 'half' ? 'Date' : 'End Date' }}
                  <span class="text-danger">*</span>
                </label>
                <input
                    type="date"
                    class="form-control"
                    id="endDate"
                    v-model="formData.endDate"
                    :min="formData.startDate || minDate"
                    :disabled="durationType === 'half'"
                    @change="calculateDays"
                    required
                >
              </div>
            </div>

            <!-- Half Day Options (shown only when half day is selected) -->
            <div v-if="durationType === 'half'" class="row mb-3">
              <div class="col-md-12">
                <label class="form-label">Half Day Session</label>
                <div class="d-flex gap-3">
                  <div class="form-check">
                    <input
                        class="form-check-input"
                        type="radio"
                        id="morning"
                        value="morning"
                        v-model="halfDaySession"
                        checked
                    >
                    <label class="form-check-label" for="morning">Morning (First Half)</label>
                  </div>
                  <div class="form-check">
                    <input
                        class="form-check-input"
                        type="radio"
                        id="afternoon"
                        value="afternoon"
                        v-model="halfDaySession"
                    >
                    <label class="form-check-label" for="afternoon">Afternoon (Second Half)</label>
                  </div>
                </div>
              </div>
            </div>

            <!-- Calculated Days Display -->
            <div class="row mb-3">
              <div class="col-md-12">
                <div class="alert alert-info py-2">
                  <i class="mdi mdi-information-outline me-2"></i>
                  <strong>Total Days Requested:</strong> {{ calculatedDays }} day(s)
                  <span v-if="calculatedDays > getAvailableDays()" class="text-danger ms-2">
                    (Exceeds available balance!)
                  </span>
                </div>
              </div>
            </div>

            <!-- Reason for Leave -->
            <div class="row mb-3">
              <div class="col-md-12">
                <label for="reason" class="form-label">Reason for Leave <span class="text-danger">*</span></label>
                <textarea
                    class="form-control"
                    id="reason"
                    v-model="formData.reason"
                    rows="4"
                    placeholder="Please provide a brief explanation for your leave request..."
                    required
                    maxlength="500"
                ></textarea>
                <small class="text-muted">{{ formData.reason.length }}/500 characters</small>
              </div>
            </div>

            <!-- Supporting Documents (Optional) -->
            <div class="row mb-3">
              <div class="col-md-12">
                <label for="documents" class="form-label">Supporting Documents (Optional)</label>
                <input
                    type="file"
                    class="form-control"
                    id="documents"
                    @change="handleFileUpload"
                    accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                    multiple
                >
                <small class="text-muted">Accepted formats: PDF, JPG, PNG, DOC, DOCX (Max 5MB each)</small>
              </div>
            </div>

            <!-- Contact During Leave -->
            <div class="row">
              <div class="col-md-6">
                <label for="contactNumber" class="form-label">Contact Number During Leave</label>
                <input
                    type="tel"
                    class="form-control"
                    id="contactNumber"
                    v-model="formData.contactNumber"
                    placeholder="Enter phone number"
                >
              </div>

              <div class="col-md-6">
                <label for="alternateContact" class="form-label">Alternate Contact Person</label>
                <input
                    type="text"
                    class="form-control"
                    id="alternateContact"
                    v-model="formData.alternateContact"
                    placeholder="Name and contact"
                >
              </div>
            </div>

            <!-- Warning Message -->
            <div v-if="showWarning" class="alert alert-warning mt-3 py-2">
              <i class="mdi mdi-alert-outline me-2"></i>
              {{ warningMessage }}
            </div>
          </div>

          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" @click="resetForm" data-bs-dismiss="modal">
              Cancel
            </button>
            <button
                type="submit"
                class="btn btn-primary"
                :disabled="loading || !isFormValid"
            >
              <span v-if="loading" class="spinner-border spinner-border-sm me-2" role="status"></span>
              {{ loading ? 'Submitting...' : 'Submit Application' }}
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
/* eslint-disable */
import { authUrl, execute } from "@/api";

export default {
  name: 'LeaveApplicationModal',
  props: {
    employeeId: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      loading: false,
      leaveTypes: [],
      leaveBalances: [],
      formData: {
        leaveTypeID: '',
        leaveTypeName: '',
        startDate: '',
        endDate: '',
        reason: '',
        contactNumber: '',
        alternateContact: '',
        documents: []
      },
      durationType: 'full',
      halfDaySession: 'morning',
      calculatedDays: 0,
      showWarning: false,
      warningMessage: '',
      minDate: '',
      selectedLeaveType: null
    };
  },
  computed: {
    isFormValid() {
      return (
          this.formData.leaveTypeID &&
          this.formData.startDate &&
          this.formData.endDate &&
          this.formData.reason.trim().length > 0 &&
          this.calculatedDays > 0 &&
          this.calculatedDays <= this.getAvailableDays()
      );
    }
  },
  watch: {
    durationType(newVal) {
      if (newVal === 'half') {
        this.formData.endDate = this.formData.startDate;
        this.calculatedDays = 0.5;
      } else {
        this.calculateDays();
      }
    }
  },
  mounted() {
    this.setMinDate();
    this.fetchLeaveTypes();
    this.fetchLeaveBalances();
  },
  methods: {
    setMinDate() {
      const today = new Date();
      today.setDate(today.getDate() + 1); // Minimum is tomorrow
      this.minDate = today.toISOString().split('T')[0];
    },

    async fetchLeaveTypes() {
      const data = new FormData();
      data.append("function", "getActiveLeaveTypes");

      try {
        const res = await execute(data, authUrl);
        if (res.data.success) {
          this.leaveTypes = res.data.data.leaveTypes;
        }
      } catch (error) {
        console.error('Error fetching leave types:', error);
      }
    },

    async fetchLeaveBalances() {
      const data = new FormData();
      data.append("function", "getEmployeeLeaveBalances");
      data.append("employeeId", this.employeeId);

      try {
        const res = await execute(data, authUrl);
        if (res.data.success) {
          // Handle nested structure - data.leaveBalances
          this.leaveBalances = res.data.data?.leaveBalances || [];
        }
      } catch (error) {
        console.error('Error fetching leave balances:', error);
        this.leaveBalances = [];
      }
    },

    onLeaveTypeChange() {
      this.selectedLeaveType = this.leaveTypes.find(t => t.id == this.formData.leaveTypeID);
      if (this.selectedLeaveType) {
        this.formData.leaveTypeName = this.selectedLeaveType.typeName;
      }
      this.checkLeaveBalance();
    },

    getAvailableDays() {
      if (!this.formData.leaveTypeID) return 0;
      const balance = this.leaveBalances.find(b => b.leaveTypeID == this.formData.leaveTypeID);
      return balance ? parseFloat(balance.remainingDays) : 0;
    },

    getAvailableDaysForType(leaveTypeId) {
      const balance = this.leaveBalances.find(b => b.leaveTypeID == leaveTypeId);
      return balance ? parseFloat(balance.remainingDays) : 0;
    },

    calculateDays() {
      if (!this.formData.startDate || !this.formData.endDate) {
        this.calculatedDays = 0;
        return;
      }

      if (this.durationType === 'half') {
        this.calculatedDays = 0.5;
        this.formData.endDate = this.formData.startDate;
        return;
      }

      const start = new Date(this.formData.startDate);
      const end = new Date(this.formData.endDate);
      let days = 0;

      for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
        const dayOfWeek = d.getDay();
        if (dayOfWeek !== 0 && dayOfWeek !== 6) { // Exclude weekends
          days++;
        }
      }

      this.calculatedDays = days;
      this.checkLeaveBalance();
    },

    checkLeaveBalance() {
      const available = this.getAvailableDays();
      if (this.calculatedDays > available) {
        this.showWarning = true;
        this.warningMessage = `You only have ${available} days available for this leave type.`;
      } else if (this.calculatedDays > 0 && this.calculatedDays <= available) {
        this.showWarning = false;
        this.warningMessage = '';
      }
    },

    handleFileUpload(event) {
      const files = event.target.files;
      const maxSize = 5 * 1024 * 1024; // 5MB

      for (let file of files) {
        if (file.size > maxSize) {
          alert(`File ${file.name} exceeds 5MB limit`);
          event.target.value = '';
          return;
        }
      }

      this.formData.documents = files;
    },

    async submitLeaveRequest() {
      if (!this.isFormValid) return;

      this.loading = true;

      const data = new FormData();
      data.append("function", "submitLeaveRequest");
      data.append("leaveTypeID", this.formData.leaveTypeID);
      data.append("leaveTypeName", this.formData.leaveTypeName);
      data.append("startDate", this.formData.startDate);
      data.append("endDate", this.formData.endDate);
      data.append("reason", this.formData.reason);
      data.append("totalDays", this.calculatedDays);
      data.append("durationType", this.durationType);

      if (this.durationType === 'half') {
        data.append("halfDaySession", this.halfDaySession);
      }

      if (this.formData.contactNumber) {
        data.append("contactNumber", this.formData.contactNumber);
      }

      if (this.formData.alternateContact) {
        data.append("alternateContact", this.formData.alternateContact);
      }

      // Add documents if any
      if (this.formData.documents.length > 0) {
        for (let i = 0; i < this.formData.documents.length; i++) {
          data.append("documents[]", this.formData.documents[i]);
        }
      }

      try {
        const res = await execute(data, authUrl);
        if (res.data.success) {
          this.$emit('leave-submitted', res.data.leaveRequest);
          $('#leave-application-modal').modal('hide');
          this.resetForm();

          // Show success message
          this.$parent.successMessage = 'Leave request submitted successfully!';
          $('#success-modal').modal('show');
        } else {
          throw new Error(res.data.message);
        }
      } catch (error) {
        console.error('Error submitting leave request:', error);
        this.$parent.errorMessage = error.message || 'Failed to submit leave request';
        $('#error-modal').modal('show');
      } finally {
        this.loading = false;
      }
    },

    resetForm() {
      this.formData = {
        leaveTypeID: '',
        leaveTypeName: '',
        startDate: '',
        endDate: '',
        reason: '',
        contactNumber: '',
        alternateContact: '',
        documents: []
      };
      this.durationType = 'full';
      this.halfDaySession = 'morning';
      this.calculatedDays = 0;
      this.showWarning = false;
      this.warningMessage = '';
      this.selectedLeaveType = null;

      // Reset file input
      const fileInput = document.getElementById('documents');
      if (fileInput) {
        fileInput.value = '';
      }
    }
  }
};
</script>

<style scoped>
/* Remove all backdrop effects */
:deep(.modal-backdrop) {
  display: none !important;
}

/* Ensure modal content has proper shadow for visibility */
.modal-content {
  box-shadow: 0 15px 50px rgba(0, 0, 0, 0.4);
  border: 1px solid rgba(0, 0, 0, 0.1);
}

/* Ensure modal dialog is properly positioned */
.modal-dialog {
  z-index: 1055;
}

.form-label {
  font-weight: 500;
  margin-bottom: 0.5rem;
}

.text-danger {
  color: #dc3545;
}

.alert {
  border-radius: 0.375rem;
}

.form-control:focus,
.form-select:focus {
  border-color: #86b7fe;
  box-shadow: 0 0 0 0.25rem rgba(13, 110, 253, 0.25);
}

.btn-close-white {
  filter: invert(1) grayscale(100%) brightness(200%);
}
</style>